'use client'

import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined'
import {
  FormControl,
  FormHelperText,
  InputLabel,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  type SelectChangeEvent,
  useTheme,
} from '@mui/material'
import { BarChart, Database, Edit, Home, Loader, Sliders } from 'lucide-react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import type React from 'react'
import { useEffect, useState } from 'react'
import useAuth from '../hooks/useAuth'
import { UsersApi } from '../services'

const CarIcon = ({ size }: { size?: number }) => (
  <DirectionsCarOutlinedIcon sx={{ fontSize: size }} />
)

interface SidebarItemsProps {
  isCollapsed: boolean
  onItemClick?: () => void // ✅ Add this to fix TypeScript error
}

const items = [
  {
    icon: Home,
    title: 'Hub Home',
    path: '/homehub',
    roles: [
      'System Administrator',
      'Super Admin',
      'supplier',
      'source',
      'annieonly',
    ],
    partnerTypes: ['source', 'supplier', 'Admin', 'Demo'],
  },
  {
    icon: CarIcon,
    title: 'My Claims',
    path: '/claimshire',
    roles: ['System Administrator', 'Super Admin', 'source', 'supplier'],
    partnerTypes: ['source', 'supplier', 'Admin'],
  },
  {
    icon: Edit,
    title: 'New Request',
    path: '/request',
    roles: ['System Administrator', 'Super Admin', 'supplier'],
    partnerTypes: ['Admin'],
  },
  {
    icon: BarChart,
    title: 'High Level Stats',
    path: '/highlevelstats',
    roles: ['System Administrator', 'Super Admin', 'source', 'supplier'],
    partnerTypes: ['source', 'supplier', 'Admin'],
  },
  {
    icon: Sliders,
    title: 'KPI Dashboard',
    path: '/kpidashboard',
    roles: ['System Administrator', 'Super Admin', 'source', 'supplier'],
    partnerTypes: ['source', 'supplier', 'Admin'],
  },
  {
    icon: Loader,
    title: 'Demo',
    path: '/demo',
    roles: [
      'System Administrator',
      'Super Admin',
      'source',
      'supplier',
      'annieonly',
    ],
    partnerTypes: ['source', 'supplier', 'Admin', 'Demo'],
  },
  {
    icon: Database,
    title: 'Admin Panel',
    path: '/admin',
    roles: ['System Administrator', 'Super Admin'],
    partnerTypes: ['Admin'],
  },
]

const SidebarItems: React.FC<SidebarItemsProps> = ({
  isCollapsed,
  onItemClick,
}) => {
  const theme = useTheme()
  const pathname = usePathname()
  const {
    user: currentUser,
    effectivePartnerType,
    setEffectivePartnerType,
  } = useAuth()
  const [_isPGViewOpen, _setIsPGViewOpen] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  // Check if user has multiple roles that could be switched between
  const hasMultipleSwitchableRoles =
    currentUser?.rolenames?.includes('source') &&
    currentUser?.rolenames?.includes('supplier')

  // Check if user is Super Admin or System Administrator (gets full access and dropdown)
  const isSuperAdmin =
    currentUser?.rolenames?.includes('Super Admin') ||
    currentUser?.rolenames?.includes('System Administrator')

  // User can switch contexts if they have multiple switchable roles OR are Super Admin/System Administrator
  const canSwitchContext = hasMultipleSwitchableRoles || isSuperAdmin

  // Get available roles for the dropdown
  const availableRoles = [
    'source',
    'supplier',
    'Super Admin',
    'System Administrator',
  ].filter((role) => currentUser?.rolenames?.includes(role))

  // Set default partner type if no preference is stored
  useEffect(() => {
    if (!effectivePartnerType && currentUser && availableRoles.length > 0) {
      // Only set default if there's no stored preference and user data is loaded
      // The database value should already be set by useAuth hook
      // This is just a fallback in case something went wrong
      // Prefer System Administrator over Super Admin for new RBAC system
      const defaultType = currentUser?.rolenames?.includes(
        'System Administrator',
      )
        ? 'System Administrator'
        : currentUser?.rolenames?.includes('Super Admin')
          ? 'Super Admin'
          : availableRoles[0]

      console.log(
        '🔧 SidebarItems: Setting default effectivePartnerType:',
        defaultType,
        'from roles:',
        currentUser?.rolenames,
      )
      setEffectivePartnerType(defaultType)
    }
  }, [
    effectivePartnerType,
    setEffectivePartnerType,
    currentUser,
    availableRoles,
  ])

  const handlePartnerTypeChange = async (event: SelectChangeEvent<string>) => {
    const selectedType = event.target.value || 'Demo' // ✅ Ensure it never stays null

    console.log('🔄 Updating effectivePartnerType:', selectedType)
    console.log(
      '📊 Before change - current effectivePartnerType:',
      effectivePartnerType,
    )

    // Update state immediately for UI responsiveness
    setEffectivePartnerType(selectedType)

    console.log('📊 After setEffectivePartnerType - should trigger re-render')

    try {
      await new UsersApi().updateUserMe({
        requestBody: { effectivePartnerType: selectedType },
      })
      console.log('✅ Updated API with effectivePartnerType:', selectedType)
      // Remove the page reload - let React handle the state updates
      // The components will re-render automatically when effectivePartnerType changes
    } catch (error: any) {
      console.error('❌ Error updating partner type:', error)
      // On error, you might want to revert the state change
      // setEffectivePartnerType(previousValue) - but we'll keep it simple for now
    }
  }

  if (!mounted) {
    return <List />
  }

  return (
    <List>
      {items
        .filter((item) => item.roles.includes(effectivePartnerType || 'source'))
        .map(({ icon: IconComponent, title, path }) => (
          <Link href={path} key={title} style={{ textDecoration: 'none' }}>
            <ListItemButton
              sx={{
                color: theme.palette.text.primary,
                backgroundColor:
                  pathname === path
                    ? theme.palette.action.selected
                    : 'transparent',
                justifyContent: isCollapsed ? 'center' : 'flex-start',
                padding: isCollapsed ? '8px' : '8px 16px',
                '&:hover': { backgroundColor: theme.palette.action.hover },
              }}
              onClick={onItemClick} // ✅ Auto-close sidebar when clicking a link
            >
              <ListItemIcon
                sx={{
                  justifyContent: 'center',
                  minWidth: isCollapsed ? 0 : 48,
                }}
              >
                <IconComponent size={isCollapsed ? 24 : 20} />
              </ListItemIcon>
              {!isCollapsed && <ListItemText primary={title} />}
            </ListItemButton>
          </Link>
        ))}

      {/* Dropdown for context switching - supports both role and partner type switching */}
      {canSwitchContext && !isCollapsed && (
        <FormControl fullWidth margin="normal" sx={{ paddingX: 2 }}>
          <InputLabel htmlFor="type">Context</InputLabel>
          <Select
            id="type"
            label="Context"
            value={effectivePartnerType || availableRoles[0] || 'source'}
            onChange={handlePartnerTypeChange}
          >
            {/* Generate menu items based on user's roles */}
            {availableRoles.map((role) => (
              <MenuItem key={role} value={role}>
                {role}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            Select role context for current session
          </FormHelperText>
        </FormControl>
      )}
    </List>
  )
}

export default SidebarItems
